import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

const clamp = (value: number) => Math.min(100, Math.max(0, value));

/**
 * Before/after divider position (0-100%). Pointer events cover mouse, touch and pen;
 * arrow keys move it when the slider has focus.
 */
export function useSliderPosition(initial = 50) {
  const [position, setPosition] = useState(initial);
  const dragging = useRef(false);

  const moveTo = (element: HTMLElement, clientX: number) => {
    const rect = element.getBoundingClientRect();
    if (rect.width > 0) setPosition(clamp(((clientX - rect.left) / rect.width) * 100));
  };

  const handlers = {
    onPointerDown(event: PointerEvent<HTMLElement>) {
      dragging.current = true;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      moveTo(event.currentTarget, event.clientX);
    },
    onPointerMove(event: PointerEvent<HTMLElement>) {
      if (dragging.current) moveTo(event.currentTarget, event.clientX);
    },
    onPointerUp(event: PointerEvent<HTMLElement>) {
      dragging.current = false;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    },
    onPointerCancel() {
      dragging.current = false;
    },
    onKeyDown(event: KeyboardEvent<HTMLElement>) {
      const step = event.shiftKey ? 10 : 2;
      const next: Record<string, (p: number) => number> = {
        ArrowLeft: (p) => p - step,
        ArrowRight: (p) => p + step,
        Home: () => 0,
        End: () => 100,
      };
      const update = next[event.key];
      if (!update) return;
      event.preventDefault();
      setPosition((p) => clamp(update(p)));
    },
  };

  return { position, handlers };
}

export type SliderState = ReturnType<typeof useSliderPosition>;
